import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { TrackingHandler } from 'client/tracking/handler';
import {
  MMZ_TRACKING_IDS,
  TrackingMap,
  getQb66TrackingMapOverridesByChalRecipe,
} from 'client/tracking/maps/new-cars-landing/new-cars-landing';
import { HomepageAdExtensionMap } from 'client/tracking/maps/common/homepage-ad-extension';
import { TopLinksSection } from 'site-modules/shared/components/top-links-section/top-links-section';
import { QB_66_LINK_OVERRIDES_BY_CHAL_RECIPE } from 'site-modules/shared/constants/top-links-section-qb-66/constants';
import { ArticlesModule } from 'site-modules/shared/components/articles-module/articles-module';
import { HelpfulTipsModule } from 'site-modules/shared/components/helpful-tips-module/helpful-tips-module';
import { BestCars } from 'site-modules/new-cars-landing/components/best-cars/best-cars';
import { RecommendedCategories } from 'site-modules/new-cars-landing/components/recommended-categories/recommended-categories';
import { PopularSearches } from 'site-modules/new-cars-landing/components/popular-searches/popular-searches';
import { MoreInfoSection } from 'site-modules/new-cars-landing/components/more-info-section/more-info-section';
import { HomeMarquee } from 'site-modules/shared/components/upper-funnel/home-marquee/home-marquee';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { SearchByModuleContent } from 'site-modules/shared/components/search-by-module/search-by-module-content';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { NewUsedLandingHero } from 'site-modules/shared/components/new-used-landing-hero/new-used-landing-hero';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';

import './index.scss';

export const NEW_LANDING_CONTENT_FILE_PATH = 'new-cars-landing/index';
export const NEW_LANDING_MORE_ABOUT_PATH = '/new-cars-landing/articles/quick-guide-to-buying-a-new-car';

const DEFAULT_H1_TITLE = 'New Car Pricing';
const DEFAULT_SUBTITLE = 'Discover what you should pay for top rated new cars.';
const DEFAULT_TOP_LINKS_TITLE = 'New car buying tools';
const TOP_LINKS_TRACKING_MAP = {
  id: { all: 'link_select_tool_select' },
  value: { 'Price Checker™': 'Price Checker' },
};
const getQb66TopLinksTrackingMapOverridesByChalRecipe = chalRecipe => {
  const chalOverrideLinkTitle = QB_66_LINK_OVERRIDES_BY_CHAL_RECIPE[chalRecipe]?.override?.title;
  if (chalOverrideLinkTitle === undefined) {
    return null; // ctrl = do NOT override
  }
  return {
    id: {
      [chalOverrideLinkTitle]: 'link_select_tool_select_qb66',
      all: TOP_LINKS_TRACKING_MAP.id.all, // Preserve fallback tracking for All OTHER links
    },
    value: { [chalOverrideLinkTitle]: chalOverrideLinkTitle },
  };
};

function NewCarPricingPageUI({
  content = null,
  moreAboutContent = DEFAULT_CONTENT,
  moreAboutAuthorContent = DEFAULT_CONTENT,
  isMobile = false,
  location = {},
  qb66Recipe = 'ctrl',
}) {
  useEffect(() => {
    const qb66TrackingMapOverride = getQb66TrackingMapOverridesByChalRecipe(qb66Recipe);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    TrackingHandler.useMap({ ...TrackingMap, ...HomepageAdExtensionMap, ...qb66TrackingMapOverride });
  }, [qb66Recipe]);

  if (!content) {
    return null;
  }

  const h1Title = get(content, 'title', DEFAULT_H1_TITLE);
  const pageSubTitle = get(content, 'subtitle', DEFAULT_SUBTITLE);
  const breadcrumbs = content.child('breadcrumb');
  const topLinks = content.child('top-links');
  const topLinksTitle = get(topLinks, 'title', DEFAULT_TOP_LINKS_TITLE);
  const helpfulArticlesContent = content.child('article-grid');
  const helpfulTipsContent = content.child('article-list');
  const bestCarsContent = content.child('price-range');
  const recommendedCategoriesChild = content.child('recommended-categories');
  const recommendedCategoriesTitle = recommendedCategoriesChild.title;
  const recommendedCategoriesContent = recommendedCategoriesChild.getAllMetadata();
  const popularSearchesChildElement = content.child('compare-popular');
  const popularSearchesContent = popularSearchesChildElement.getAllMetadata();
  const popularSearchesContentTitle = popularSearchesChildElement.title;

  const qb66TopLinksTrackingMapOverride = getQb66TopLinksTrackingMapOverridesByChalRecipe(qb66Recipe);

  return (
    <main id="main-content" tabIndex={-1} className="new-car-pricing-page" data-tracking-parent="edm-new-cars-landing">
      <NewUsedLandingHero
        title={h1Title}
        subTitle={pageSubTitle}
        breadcrumbsData={breadcrumbs.links()}
        trackingParent="edm-new-cars-index-hero"
      >
        <SearchByModuleContent
          className="bg-white overlap-module border-0 p-1_5"
          tabsContainerClassName="mb-1_25"
          isMobile={isMobile}
          trackingMap={MMZ_TRACKING_IDS}
          hasCorePageRedirect
          location={location}
        />
      </NewUsedLandingHero>
      <Container>
        <TopLinksSection
          content={topLinks}
          title={topLinksTitle}
          tracking={qb66TopLinksTrackingMapOverride || TOP_LINKS_TRACKING_MAP}
          qb66Recipe={qb66Recipe}
        />
        <Experiment name="QB-66-NCL-V4-Signup" showDefault>
          {/* Empty recipe tags preserved for WTF pixels/bucketing, as logic is defined outside: */}
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal-1" />
          <Recipe name="chal-2" />
          <Recipe name="chal-3" />
          <Recipe name="chal-4" />
          <Recipe name="chal-5" />
          <Recipe name="chal-6" />
        </Experiment>
        <hr className="mt-1_5 mb-2" />
        <Row>
          <Col xs={12}>
            <RecommendedCategories title={recommendedCategoriesTitle} content={recommendedCategoriesContent} />
            <hr className="mt-1_5 mb-2" />
          </Col>
        </Row>
        <BestCars content={bestCarsContent} />
        <hr className="mt-0 mb-2" />
        <Row>
          <Col xs={12} lg={8}>
            <ErrorBoundary>
              <HomeMarquee />
              <hr className="mt-0 col-12 mb-2" />
            </ErrorBoundary>
            <ArticlesModule content={helpfulArticlesContent} isLazyImages />
            <hr className="mt-1_5 mb-2 d-lg-none" />
          </Col>
          <Col xs={12} lg={4}>
            <HelpfulTipsModule content={helpfulTipsContent} className="ps-lg-2 mt-0 mt-md-1_5 mt-lg-0" />
            <MrectNative
              position={'1'}
              wrapperClass={isMobile ? 'my-1' : 'mt-2'}
              refreshable={false}
              renderWhenViewable
              verticalOffset="100%"
              useMinHeight
            />
          </Col>
          <Col xs={12}>
            <hr className="mt-1_5 mb-2" />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <PopularSearches title={popularSearchesContentTitle} content={popularSearchesContent} />
          </Col>
          <Col xs={12}>
            <hr className="mt-1_5 mb-2" />
          </Col>
        </Row>
        <Row className="mb-1_5">
          <Col xs={12} lg={8}>
            <MoreInfoSection content={moreAboutContent} authorContent={moreAboutAuthorContent} />
            {isMobile && (
              <MrectNative
                mobileOnly
                position={'2'}
                wrapperClass="mb-1"
                refreshable={false}
                renderWhenViewable
                verticalOffset="100%"
                useMinHeight
              />
            )}
          </Col>
          {!isMobile && (
            <Col xs={12} lg={4}>
              <MrectNative
                wiredOnly
                position={'2'}
                wrapperClass="mb-md-2 mt-lg-2"
                refreshable={false}
                renderWhenViewable
                verticalOffset="100%"
                useMinHeight
              />
            </Col>
          )}
        </Row>
        <hr className="mt-1_5 mb-2" />
        <PageLinksWidget divideSections={false} title="Shop new cars now" />
      </Container>
    </main>
  );
}

NewCarPricingPageUI.propTypes = {
  content: CmsEntities.Content,
  moreAboutContent: CmsEntities.Content,
  moreAboutAuthorContent: CmsEntities.Content,
  isMobile: PropTypes.bool,
  location: PropTypes.shape({}),
  qb66Recipe: PropTypes.string,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  location: get(state, 'pageContext.location'),
  qb66Recipe: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'QB-66-NCL-V4-Signup',
    defaultVal: 'ctrl',
  }),
});

export const NewCarPricingPage = connect(mapStateToProps)(
  connectToModel(NewCarPricingPageUI, {
    content: bindToContent(NEW_LANDING_CONTENT_FILE_PATH),
    moreAboutContent: bindToContent(NEW_LANDING_MORE_ABOUT_PATH),
    moreAboutAuthorContent: bindToContent(`${NEW_LANDING_MORE_ABOUT_PATH}.author`),
  })
);
